import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  SprawaDto,
  SprawaListDto,
  SprawaDetailDto,
  SprawaListParams,
  CreateSprawaRequest,
  AddPartyRequest,
  PosiedzenieDto,
  CreatePosiedzenieRequest,
  CalendarPosiedzenieDto,
  CalendarPosiedzeniaParams,
  ApelacjaDto,
  SprawaStatusDefinitionDto,
  TimeEntryDto,
  CreateTimeEntryRequest,
  UpdateTimeEntryRequest,
} from '@/types';
import { baseQueryWithRetry } from './baseQuery';
import { TAG_SPRAWA, TAG_POSIEDZENIE, TAG_SPRAWA_STATUS_DEFINITION, TAG_TIME_ENTRY } from '@/store/tagTypes';

export const sprawyApi = createApi({
  reducerPath: 'sprawyApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: [TAG_SPRAWA, TAG_POSIEDZENIE, TAG_SPRAWA_STATUS_DEFINITION, TAG_TIME_ENTRY],
  endpoints: (builder) => ({
    listSprawy: builder.query<SprawaListDto, SprawaListParams | void>({
      query: (params) => ({
        url: '/sprawy',
        params: params
          ? {
              page: params.page?.toString(),
              pageSize: params.pageSize?.toString(),
              portalStatus: params.portalStatus,
              customerId: params.customerId,
              courtId: params.courtId?.toString(),
              apelacja: params.apelacja,
              statusDefinitionId: params.statusDefinitionId,
            }
          : undefined,
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: TAG_SPRAWA, id })),
              { type: TAG_SPRAWA, id: 'LIST' },
            ]
          : [{ type: TAG_SPRAWA, id: 'LIST' }],
    }),

    listCourts: builder.query<ApelacjaDto[], void>({
      query: () => '/courts/sprawy-counts',
      providesTags: [{ type: TAG_SPRAWA, id: 'COURTS' }],
    }),

    getSprawa: builder.query<SprawaDetailDto, string>({
      query: (id) => `/sprawy/${id}`,
      providesTags: (_result, _error, id) => [{ type: TAG_SPRAWA, id }],
    }),

    createSprawa: builder.mutation<SprawaDto, CreateSprawaRequest>({
      query: (body) => ({
        url: '/sprawy',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TAG_SPRAWA, id: 'LIST' }],
    }),

    updateSprawa: builder.mutation<void, { id: string } & CreateSprawaRequest>({
      query: ({ id, ...body }) => ({
        url: `/sprawy/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TAG_SPRAWA, id },
        { type: TAG_SPRAWA, id: 'LIST' },
      ],
    }),

    deleteSprawa: builder.mutation<void, string>({
      query: (id) => ({
        url: `/sprawy/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: TAG_SPRAWA, id },
        { type: TAG_SPRAWA, id: 'LIST' },
      ],
    }),

    addPartyToSprawa: builder.mutation<void, { sprawaId: string } & AddPartyRequest>({
      query: ({ sprawaId, ...body }) => ({
        url: `/sprawy/${sprawaId}/parties`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { sprawaId }) => [{ type: TAG_SPRAWA, id: sprawaId }],
    }),

    removePartyFromSprawa: builder.mutation<void, { sprawaId: string; partyId: string }>({
      query: ({ sprawaId, partyId }) => ({
        url: `/sprawy/${sprawaId}/parties/${partyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { sprawaId }) => [{ type: TAG_SPRAWA, id: sprawaId }],
    }),

    // --- Posiedzenia ---

    listCalendarPosiedzenia: builder.query<CalendarPosiedzenieDto[], CalendarPosiedzeniaParams>({
      query: ({ from, to }) => ({
        url: '/posiedzenia/calendar',
        params: { from, to },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TAG_POSIEDZENIE, id })),
              { type: TAG_POSIEDZENIE, id: 'CALENDAR' },
            ]
          : [{ type: TAG_POSIEDZENIE, id: 'CALENDAR' }],
    }),

    listPosiedzenia: builder.query<PosiedzenieDto[], string>({
      query: (sprawaId) => `/sprawy/${sprawaId}/posiedzenia`,
      providesTags: (result, _error, sprawaId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TAG_POSIEDZENIE, id })),
              { type: TAG_POSIEDZENIE, id: `LIST-${sprawaId}` },
            ]
          : [{ type: TAG_POSIEDZENIE, id: `LIST-${sprawaId}` }],
    }),

    createPosiedzenie: builder.mutation<PosiedzenieDto, { sprawaId: string } & CreatePosiedzenieRequest>({
      query: ({ sprawaId, ...body }) => ({
        url: `/sprawy/${sprawaId}/posiedzenia`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { sprawaId }) => [
        { type: TAG_POSIEDZENIE, id: `LIST-${sprawaId}` },
        { type: TAG_SPRAWA, id: sprawaId },
      ],
    }),

    updatePosiedzenie: builder.mutation<void, { sprawaId: string; id: string } & CreatePosiedzenieRequest>({
      query: ({ sprawaId, id, ...body }) => ({
        url: `/sprawy/${sprawaId}/posiedzenia/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { sprawaId, id }) => [
        { type: TAG_POSIEDZENIE, id },
        { type: TAG_POSIEDZENIE, id: `LIST-${sprawaId}` },
      ],
    }),

    deletePosiedzenie: builder.mutation<void, { sprawaId: string; id: string }>({
      query: ({ sprawaId, id }) => ({
        url: `/sprawy/${sprawaId}/posiedzenia/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { sprawaId, id }) => [
        { type: TAG_POSIEDZENIE, id },
        { type: TAG_POSIEDZENIE, id: `LIST-${sprawaId}` },
        { type: TAG_SPRAWA, id: sprawaId },
      ],
    }),

    // --- Time Entries ---

    listTimeEntries: builder.query<TimeEntryDto[], string>({
      query: (sprawaId) => `/sprawy/${sprawaId}/time-entries`,
      providesTags: (result, _error, sprawaId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TAG_TIME_ENTRY, id })),
              { type: TAG_TIME_ENTRY, id: `LIST-${sprawaId}` },
            ]
          : [{ type: TAG_TIME_ENTRY, id: `LIST-${sprawaId}` }],
    }),

    getActiveTimeEntry: builder.query<TimeEntryDto | null, void>({
      query: () => '/time-entries/active',
      providesTags: [{ type: TAG_TIME_ENTRY, id: 'ACTIVE' }],
    }),

    createTimeEntry: builder.mutation<TimeEntryDto, { sprawaId: string } & CreateTimeEntryRequest>({
      query: ({ sprawaId, ...body }) => ({
        url: `/sprawy/${sprawaId}/time-entries`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { sprawaId }) => [
        { type: TAG_TIME_ENTRY, id: `LIST-${sprawaId}` },
        { type: TAG_TIME_ENTRY, id: 'ACTIVE' },
      ],
    }),

    updateTimeEntry: builder.mutation<void, { sprawaId: string; id: string } & UpdateTimeEntryRequest>({
      query: ({ id, ...body }) => ({
        url: `/time-entries/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { sprawaId, id }) => [
        { type: TAG_TIME_ENTRY, id },
        { type: TAG_TIME_ENTRY, id: `LIST-${sprawaId}` },
      ],
    }),

    deleteTimeEntry: builder.mutation<void, { sprawaId: string; id: string }>({
      query: ({ id }) => ({
        url: `/time-entries/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { sprawaId, id }) => [
        { type: TAG_TIME_ENTRY, id },
        { type: TAG_TIME_ENTRY, id: `LIST-${sprawaId}` },
        { type: TAG_TIME_ENTRY, id: 'ACTIVE' },
      ],
    }),

    stopTimeEntry: builder.mutation<TimeEntryDto, { sprawaId: string; id: string }>({
      query: ({ id }) => ({
        url: `/time-entries/${id}/stop`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { sprawaId, id }) => [
        { type: TAG_TIME_ENTRY, id },
        { type: TAG_TIME_ENTRY, id: `LIST-${sprawaId}` },
        { type: TAG_TIME_ENTRY, id: 'ACTIVE' },
      ],
    }),

    // --- Status Definitions ---

    listSprawaStatusDefinitions: builder.query<SprawaStatusDefinitionDto[], void>({
      query: () => '/sprawa-statuses',
      providesTags: [{ type: TAG_SPRAWA_STATUS_DEFINITION, id: 'LIST' }],
    }),

    createSprawaStatusDefinition: builder.mutation<SprawaStatusDefinitionDto, Omit<SprawaStatusDefinitionDto, 'id'>>({
      query: (body) => ({
        url: '/sprawa-statuses',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TAG_SPRAWA_STATUS_DEFINITION, id: 'LIST' }],
    }),

    updateSprawaStatusDefinition: builder.mutation<void, SprawaStatusDefinitionDto>({
      query: ({ id, ...body }) => ({
        url: `/sprawa-statuses/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: TAG_SPRAWA_STATUS_DEFINITION, id: 'LIST' }],
    }),

    deleteSprawaStatusDefinition: builder.mutation<void, string>({
      query: (id) => ({
        url: `/sprawa-statuses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: TAG_SPRAWA_STATUS_DEFINITION, id: 'LIST' }],
    }),

    updateSprawaMyStatus: builder.mutation<void, { sprawaId: string; statusDefinitionId: string }>({
      query: ({ sprawaId, statusDefinitionId }) => ({
        url: `/sprawy/${sprawaId}/status`,
        method: 'PATCH',
        body: { statusDefinitionId },
      }),
      invalidatesTags: (_result, _error, { sprawaId }) => [
        { type: TAG_SPRAWA, id: sprawaId },
        { type: TAG_SPRAWA, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListSprawyQuery,
  useListCourtsQuery,
  useListCalendarPosiedzeniaQuery,
  useGetSprawaQuery,
  useCreateSprawaMutation,
  useUpdateSprawaMutation,
  useDeleteSprawaMutation,
  useAddPartyToSprawaMutation,
  useRemovePartyFromSprawaMutation,
  useListPosiedzeniaQuery,
  useCreatePosiedzenieMutation,
  useUpdatePosiedzenieMutation,
  useDeletePosiedzenieMutation,
  useListSprawaStatusDefinitionsQuery,
  useCreateSprawaStatusDefinitionMutation,
  useUpdateSprawaStatusDefinitionMutation,
  useDeleteSprawaStatusDefinitionMutation,
  useUpdateSprawaMyStatusMutation,
  useListTimeEntriesQuery,
  useGetActiveTimeEntryQuery,
  useCreateTimeEntryMutation,
  useUpdateTimeEntryMutation,
  useDeleteTimeEntryMutation,
  useStopTimeEntryMutation,
} = sprawyApi;
